import { useTranslation } from "react-i18next";
import * as React from "react";
import { formatDate } from "@/lib/date-utils";
import { DescriptionSection } from "./sections/DescriptionSection";
import { FlexibleNotesSection } from "./sections/FlexibleNotesSection";
import { PricingBreakdownSection } from "./sections/PricingBreakdownSection";
import { QuoteTotalSection } from "./sections/QuoteTotalSection";
import { TemplateTotalSection } from "./sections/TemplateTotalSection";
import { ProfessionalQuoteLayout } from "./ViewerLayout";
import type { Quote, Service, CustomPart, Customer } from "@onsemble/shared";
import { QuoteTemplate, STATUS_COLORS } from "@shared/schema";
import { useStatusLabel } from "@/lib/use-status-label";
import { PricingService } from "@shared/pricing-service";
import { useQuotePricing } from "@/hooks/use-pricing";
import { Product } from "@/types/product";

// Extended types for quotes/templates with organization data
interface QuoteWithOrganization extends Quote {
  organization?: {
    name?: string;
    imageUrl?: string;
    address?: {
      streetAddress?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    } | null;
    phone?: string | null;
  };
}

interface QuoteTemplateWithOrganization extends QuoteTemplate {
  organization?: {
    name?: string;
    imageUrl?: string;
    address?: {
      streetAddress?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    } | null;
    phone?: string | null;
  };
}

interface QuoteViewerProps {
  data: QuoteWithOrganization | QuoteTemplateWithOrganization | any; // Allow form data for preview
  viewMode: "internal" | "public" | "preview";
  customer?: Partial<Customer>;
  // Actions rendered as children for maximum flexibility
  actions?: React.ReactNode;

  // Data props - all required for consistent pricing calculations
  products?: Product[];
  services: Service[]; // Required: service catalog data
  customParts: CustomPart[]; // Required: custom parts catalog data

  // Loading state props - required for coordinated data loading
  productsLoading?: boolean;
  servicesLoading?: boolean; // Required: prevents premature calculations
  customPartsLoading?: boolean; // Required: prevents premature calculations

  // Error handling props
  productsError?: Error | null;
  servicesError?: Error | null;
  customPartsError?: Error | null;

  // Display customization
  hidePartDetails?: boolean; // For public approval page
  className?: string;
}

/**
 * QuoteViewer - Universal quote/template viewing component
 *
 * Can be used standalone or wrapped in a dialog/modal.
 * Supports three modes: internal (authenticated), public (customer approval), preview (form preview)
 *
 * @example
 * // Standalone usage
 * <QuoteViewer data={quote} viewMode="internal" actions={<EditButton />} />
 *
 * // In a dialog
 * <Dialog>
 *   <DialogContent>
 *     <QuoteViewer data={quote} viewMode="internal" />
 *   </DialogContent>
 * </Dialog>
 *
 * // Public approval page
 * <QuoteViewer
 *   data={quote}
 *   viewMode="public"
 *   actions={<><ApproveButton /><RejectButton /></>}
 * />
 */
export const QuoteViewer: React.FC<QuoteViewerProps> = ({
  data,
  viewMode,
  customer,
  actions,
  products = [],
  services,
  customParts,
  productsLoading = false,
  servicesLoading = false,
  customPartsLoading = false,
  productsError: _productsError = null,
  servicesError: _servicesError = null,
  customPartsError: _customPartsError = null,
  hidePartDetails = false,
  className = "",
}) => {
  // All hooks must run on every render — declare them above any early returns
  // to satisfy React's rules of hooks. useQuotePricing tolerates undefined
  // services/customParts; its result is simply unused when we early-return below.
  const { t } = useTranslation();
  const formatQuoteStatus = useStatusLabel("quote");
  const pricingCalculation = useQuotePricing(
    data,
    products,
    services,
    customParts,
  );

  // Wait for all required data to load before rendering
  if (productsLoading || servicesLoading || customPartsLoading) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t("features.quotes.quoteViewer.index.loadingQuoteData")}</p>
          </div>
        </div>
      </div>
    );
  }

  // Verify all data is present before proceeding
  if (!services || !customParts) {
    console.error("QuoteViewer: Missing required data", {
      services,
      customParts,
    });
    return (
      <div className={className}>
        <div className="border border-destructive bg-destructive/10 rounded-lg p-6">
          <p className="text-destructive font-medium">
            {t("features.quotes.quoteViewer.index.errorMissingRequiredData")}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            {t("features.quotes.quoteViewer.index.services")} {services ? "✓" : "✗"}{t("features.quotes.quoteViewer.index.customParts")}{" "}
            {customParts ? "✓" : "✗"}
          </p>
        </div>
      </div>
    );
  }

  // Determine entity type
  const isQuote = "quoteStatus" in data;
  const isTemplate = "name" in data && !("quoteStatus" in data);
  const isPreview = viewMode === "preview";

  // Determine if quote prices are locked (embedded pricing)
  const isPricesLocked = isQuote && !!(data as Quote).pricesLockedAt;

  // Helper to safely get the title/name based on type
  const getTitle = () => {
    if (isQuote) {
      return (data as Quote).title;
    }
    if (isTemplate) {
      return (data as QuoteTemplate).name;
    }
    // For preview, use the title from form data
    return data.title || "New Quote";
  };

  // Helper to get notes field based on type
  const getNotes = () => {
    if (isQuote) {
      const notes = (data as Quote).notes;
      return notes?.trim() || null;
    }
    if (isTemplate) {
      const notes = (data as QuoteTemplate).defaultNotes;
      return notes?.trim() || null;
    }
    // For preview, use notes from form data
    return data.notes?.trim() || null;
  };

  // Helper to enrich quote products with actual product information and post-markup pricing
  const getEnrichedProducts = () => {
    if (!data.products) return [];

    // If prices are locked, use embedded pricing from products array
    if (isPricesLocked) {
      // Check if products have embedded pricing data (postMarkupTotalPrice is the final calculated value)
      const hasEmbeddedPricing =
        data.products.length > 0 &&
        data.products.some((p: any) => p.postMarkupTotalPrice !== undefined);

      if (hasEmbeddedPricing) {
        // Return products with embedded pricing directly
        return data.products.map((p: any) => ({
          ...p,
          name: p.description,
          description: p.description,
          quantity: p.quantity || 1,
          postMarkupUnitPrice: p.postMarkupUnitPrice || 0,
          postMarkupTotalPrice: p.postMarkupTotalPrice || 0,
          hajocaProductId: p.hajocaProductId,
        }));
      }
      // Fall through to live pricing if embedded data missing (backward compatibility)
    }

    // For draft quotes or quotes without embedded pricing: use current prices
    // IMPORTANT: Check if products are still loading - don't try to enrich with empty array
    if (!products || products.length === 0 || productsLoading) return [];

    // Use PricingService to calculate post-markup prices
    const { products: enhancedProducts } =
      PricingService.calculateProductPricing(
        data.products,
        products,
        data.markupPercent || 0,
        data.partsAffectPrice ?? true,
      );

    return enhancedProducts;
  };

  // Helper to enrich quote services with actual service information
  const getEnrichedServices = () => {
    if (!services || !data.quoteServices) return [];

    // quoteServices is always in standard format: [{id: 's1', name: 'Service', price: 5000}]
    // Both database and preview mode use this format
    return data.quoteServices.map((quoteService: any) => {
      const serviceDetails = services.find((s) => s.uid === quoteService.id);

      if (!serviceDetails) {
        return {
          ...quoteService,
          name: quoteService.name || `Service ${quoteService.id}`,
          price: quoteService.price || 0,
        };
      }

      return {
        ...quoteService,
        name: quoteService.name || serviceDetails.title,
        description: serviceDetails.description,
        // Use stored price from quoteService (either custom or default at time of creation)
        price:
          quoteService.price !== undefined && quoteService.price !== null
            ? quoteService.price
            : serviceDetails.cost || 0,
      };
    });
  };

  // Helper to enrich quote custom parts with actual custom part information and post-markup pricing
  const getEnrichedCustomParts = () => {
    if (!data.quoteCustomParts || !customParts) return [];

    // Use PricingService to calculate post-markup prices - same pattern as products
    const { customParts: enhancedCustomParts } =
      PricingService.calculateCustomPartPricing(
        data.quoteCustomParts, // Raw quote custom parts data from backend
        customParts, // Full custom parts array for lookup
        data.markupPercent || 0,
        data.partsAffectPrice ?? true,
      );

    return enhancedCustomParts;
  };

  // Helper to get display amounts for QuoteTotalSection
  // For draft quotes and templates: recalculates totals from current prices using PricingService
  // For locked quotes: uses stored historical amounts
  const getDisplayAmounts = () => {
    // Only locked quotes use stored amounts
    if (isPricesLocked) {
      return {
        amountPrediscount: (data as Quote).amountPrediscount,
        amountPresalesTax: (data as Quote).amountPresalesTax,
        amountPostsalesTax: (data as Quote).amountPostsalesTax,
        taxAmount: (data as Quote).taxAmount,
      };
    }

    // For draft quotes and templates: use PricingService calculation (via useQuotePricing hook above)
    // This ensures consistency with QuoteEditor and shows live pricing for templates
    // Tax is still historical (immutable) for quotes - always use stored taxAmount if available
    const taxAmount = isQuote ? (data as Quote).taxAmount || 0 : 0;
    const amountPostsalesTax =
      pricingCalculation.subtotalAfterDiscount + taxAmount;

    return {
      amountPrediscount: pricingCalculation.subtotalBeforeDiscount,
      amountPresalesTax: pricingCalculation.subtotalAfterDiscount,
      amountPostsalesTax: amountPostsalesTax,
      taxAmount,
    };
  };

  return (
    <div className={className}>
      <ProfessionalQuoteLayout
        title={getTitle()}
        quoteNumber={isQuote ? (data as Quote).quoteNumber : undefined}
        date={
          isPreview
            ? formatDate(new Date())
            : data.creationTime
              ? formatDate(data.creationTime)
              : undefined
        }
        status={
          isPreview
            ? t("features.quotes.quoteViewer.index.preview")
            : isQuote
              ? formatQuoteStatus((data as Quote).quoteStatus)
              : t("common.template")
        }
        statusColor={
          isPreview
            ? "bg-blue-100 text-blue-800"
            : isQuote
              ? STATUS_COLORS[(data as Quote).quoteStatus]
              : "bg-gray-100 text-gray-800"
        }
        customer={customer}
        showCompanyHeader={true}
        organization={data.organization}
      >
        <DescriptionSection description={data.description} />

        <PricingBreakdownSection
          data={{
            ...data,
            products: getEnrichedProducts(),
            quoteServices: getEnrichedServices(),
            quoteCustomParts: getEnrichedCustomParts(),
          }}
          hidePartDetails={hidePartDetails}
          isPricesLocked={isPricesLocked}
        />

        {/* Quote/Template/Preview amount */}
        {isQuote || isPreview ? (
          <>
            <QuoteTotalSection data={getDisplayAmounts()} />
            {isPreview && (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 mt-4">
                <p className="text-sm text-amber-800">
                  <strong>{t("features.quotes.quoteViewer.index.note")}</strong> {t("features.quotes.quoteViewer.index.salesTaxWillBe")}
                </p>
              </div>
            )}
          </>
        ) : (
          <TemplateTotalSection
            data={{
              amountPrediscount: pricingCalculation.subtotalBeforeDiscount,
              amountPresalesTax: pricingCalculation.subtotalAfterDiscount,
            }}
          />
        )}

        {getNotes() && <FlexibleNotesSection notes={getNotes()!} />}
      </ProfessionalQuoteLayout>

      {/* Render actions if provided */}
      {actions && <div className="mt-6 pt-6 border-t">{actions}</div>}
    </div>
  );
};
